import { useEffect, useState, useCallback } from 'react';
import { getPendingSubmissions, deleteSubmissionFromQueue } from '@/src/services/formStorage';
import { db } from '../services/db';
import { useAuth } from '@/src/context/AuthContext';

export const useSyncManager = () => {
    const { user } = useAuth();
    const [pendingCount, setPendingCount] = useState(0);
    const [isSyncing, setIsSyncing] = useState(false);
    const [lastError, setLastError] = useState<string | null>(null);

    const updateCount = useCallback(async () => {
        const pending = await getPendingSubmissions();
        setPendingCount(pending.length);
    }, []);

    const sync = useCallback(async () => {
        // Check network status
        if (!navigator.onLine) {
            console.log("[SyncManager] Offline. Skipping sync.");
            return;
        }

        if (!user) return;

        setIsSyncing(true);
        setLastError(null);

        try {
            const pending = await getPendingSubmissions();
            setPendingCount(pending.length); // Update count before starting

            if (pending.length === 0) {
                setIsSyncing(false);
                return;
            }

            console.log(`[SyncManager] Found ${pending.length} pending submissions. Processing...`);

            let failureCount = 0;

            for (const item of pending) {
                try {
                    // item.payload contains the arguments for submitResponse
                    await db.submitResponse(item.payload, user);

                    // If successful, remove from queue
                    await deleteSubmissionFromQueue(item.id);
                    console.log(`[SyncManager] Successfully synced item ${item.id}`);

                } catch (e) {
                    console.error(`[SyncManager] Failed to sync item ${item.id}:`, e);
                    failureCount++;
                    // We leave it in the queue.
                }
            }

            // Update count after processing
            await updateCount();

            if (failureCount > 0) {
                setLastError(`${failureCount} itens falharam ao sincronizar. Tentaremos novamente.`);
            }

        } catch (err) {
            console.error("[SyncManager] Critical error during sync cycle:", err);
            setLastError("Erro crítico na sincronização.");
        } finally {
            setIsSyncing(false);
        }
    }, [user, updateCount]);

    useEffect(() => {
        if (!user) return;

        // Run sync on mount (if online)
        sync();
        updateCount();

        // Listeners
        window.addEventListener('online', sync);
        window.addEventListener('sync-queue-change', updateCount);

        // Run sync periodically (every 60s)
        const interval = setInterval(sync, 60000);

        return () => {
            window.removeEventListener('online', sync);
            window.removeEventListener('sync-queue-change', updateCount);
            clearInterval(interval);
        };
    }, [user, sync, updateCount]);

    return { pendingCount, isSyncing, lastError, syncNow: sync };
};
